import { ExpandOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { getImageUrlFromStorage } from 'library/helpers/getImageUrlFromStorage';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { DeviceDrawer } from 'zComponents/molecules';
import { Button, Col, Modal, Row, FloatButton } from 'zui/Antd';
import { theme } from 'antd';
import { Loader } from '..';
import MapRenderer from './components/mapRenderer';
import MapWrapper, { MapModalWrapper } from './leafletMap.styles';

/**
 *	This is the Leaflet Map component. allowing you to add static custom floor plan.
 *
 * @export
 * @param {*} {
 * 	id = 'map', Optional fields
 * 	center, mandatory fields
 * 	mapHeight = 1000, height of the map image
 * 	mapWidth = 1000, width of the map image
 * 	scrollWheelZoom = false, enable or disable scroll wheel zoom
 * 	url, mandatory fields, URL of the map image
 * 	minZoom = -3, minimum zoom level
 * 	drawControlEnabled = false, enable or disable draw control
 * 	zoom = 0, default zoom level
 * 	markerList = [], list of markers
 *  draggableMarkerLocation = false, enable or disable draggable marker location
 *  editMarkerLocation =[] array of markers to be editable
 * }
 * @returns
 */
const LeafletMap = (props) => {
	const {
		id = 'map',
		center,
		mapHeight = 1000,
		mapWidth = 1000,
		scrollWheelZoom = false,
		url,
		drawControlEnabled = false,
		minZoom = -3,
		zoom = 0,
		markerList,
		drawSingleMarkerEnabled = false,
		draggableMarkerLocation = false,
		setMarker,
		editMarkerLocation,
		expandMapControl = true
	} = props;

	const {
		token: { colorBgContainer }
	} = theme.useToken();

	const mapRef = useRef(null);
	const [map, setMap] = useState(null);
	const mapModalRef = useRef(null);
	const [mapModal, setMapModal] = useState(null);
	const [visible, setVisible] = useState(false);
	const [selectedDevice, setSelectedDevice] = useState({});
	const [mapModalVisible, setMapModalVisible] = useState(false);

	// 🆕 new state to check if the url is from fire-store
	const [resolvedUrl, setResolvedUrl] = useState(null);

	// Zoom functions
	const handleZoomIn = () => {
		if (map) {
			map.zoomIn();
		}
	};

	const handleZoomOut = () => {
		if (map) {
			map.zoomOut();
		}
	};

	// Zoom functions for modal map
	const handleModalZoomIn = () => {
		if (mapModal) {
			mapModal.zoomIn();
		}
	};

	const handleModalZoomOut = () => {
		if (mapModal) {
			mapModal.zoomOut();
		}
	};

	// 🆕 Resolve the url to know if is already an public url or not
	useEffect(() => {
		const resolveUrl = async () => {
			if (!url) return;

			if (url.startsWith('http') || url.startsWith('/')) {
				setResolvedUrl(url);
			} else {
				const downloadUrl = await getImageUrlFromStorage(url);
				setResolvedUrl(downloadUrl);
			}
		};
		resolveUrl();
	}, [url]);

	// Render the map on screen
	useEffect(() => {
		if (!resolvedUrl) return;
		MapRenderer({
			mapRef,
			map,
			url: resolvedUrl, // 🆕 use resolveUrl
			markerList,
			drawControlEnabled,
			drawSingleMarkerEnabled,
			editMarkerLocation,
			draggableMarkerLocation,
			setMap,
			setMarker,
			scrollWheelZoom,
			mapHeight,
			mapWidth,
			center,
			minZoom,
			zoom,
			setSelectedDevice,
			setVisible
		});
	}, [
		resolvedUrl,
		map,
		markerList,
		selectedDevice,
		drawControlEnabled,
		drawSingleMarkerEnabled,
		editMarkerLocation,
		draggableMarkerLocation,
		scrollWheelZoom,
		mapHeight,
		mapWidth,
		center,
		minZoom,
		zoom,
		setMarker
	]);

	// Render the map in modal
	useEffect(() => {
		if (!resolvedUrl) return;
		MapRenderer({
			mapRef: mapModalRef,
			map: mapModal,
			url: resolvedUrl, // 🆕 use resolveUrl
			markerList,
			drawControlEnabled,
			drawSingleMarkerEnabled,
			editMarkerLocation,
			draggableMarkerLocation,
			setMap: setMapModal,
			setMarker,
			scrollWheelZoom,
			mapHeight,
			mapWidth,
			center,
			minZoom,
			zoom,
			setSelectedDevice,
			setVisible
		});
	}, [
		resolvedUrl,
		mapModal,
		markerList,
		selectedDevice,
		drawControlEnabled,
		drawSingleMarkerEnabled,
		editMarkerLocation,
		draggableMarkerLocation,
		scrollWheelZoom,
		mapHeight,
		mapWidth,
		center,
		minZoom,
		zoom,
		mapModalVisible,
		setMarker
	]);

	return resolvedUrl !== undefined && resolvedUrl !== null && resolvedUrl !== '' ? ( // 🆕 use resolveUrl
		<Fragment>
			{props.children}
			<MapWrapper
				ref={mapRef}
				id={id}
				style={{
					width: '100%',
					height: '100%',
					position: 'relative'
				}}
			>
				{expandMapControl && (
					<Button
						title='Expand Map'
						onClick={() => setMapModalVisible(mapModalVisible ? false : true)}
						style={{
							position: 'absolute',
							zIndex: 500,
							bottom: '10px',
							left: '10px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
						icon={<ExpandOutlined />}
					/>
				)}

				{/* Custom Zoom Controls */}
				<FloatButton.Group
					style={{
						position: 'absolute',
						width: '30px',
						height: '60px',
						bottom: '20px',
						right: '10px',
						backgroundColor: colorBgContainer,
						zIndex: 1000
					}}
					shape='square'
				>
					<FloatButton
						icon={<PlusOutlined style={{ fontSize: 14 }} />}
						onClick={handleZoomIn}
						tooltip='Zoom In'
						/>
					<FloatButton
						icon={<MinusOutlined style={{ fontSize: 14 }} />}
						onClick={handleZoomOut}
						tooltip='Zoom Out'
						/>
				</FloatButton.Group>
			</MapWrapper>
			{/* Modal to render bigger map. */}
			<Modal open={mapModalVisible} onCancel={() => setMapModalVisible(false)} footer={null} width={'80vw'}>
				<MapModalWrapper style={{ height: '80vh', width: '100%', position: 'relative' }}>
					<MapWrapper
						ref={mapModalRef}
						id={`${id}_modal`}
						style={{
							width: '100%',
							height: '100%'
						}}
					/>

					{/* Custom Zoom Controls for Modal */}
					<FloatButton.Group
						style={{
							position: 'absolute',
							top: '20px',
							left: '10px',
							width: '30px',
							height: '60px',
							backgroundColor: colorBgContainer,
							zIndex: 1000
						}}
						shape='square'
						>
						<FloatButton
							icon={<PlusOutlined style={{ fontSize: 14 }} />}
							onClick={handleModalZoomIn}
							tooltip='Zoom In'
								/>
						<FloatButton
							icon={<MinusOutlined style={{ fontSize: 14 }} />}
							onClick={handleModalZoomOut}
							tooltip='Zoom Out'
								/>
					</FloatButton.Group>
				</MapModalWrapper>
			</Modal>

			{/* Individual Device Modal  */}
			{/* {selectedDevice.uID && (
				<IndividualDeviceInformationModal
					visible={visible}
					setVisible={setVisible}
					data={{
						deviceID: selectedDevice.uID,
						categoryID: selectedDevice.categoryID
					}}
				/>
			)} */}
			{selectedDevice.uID && (
				<DeviceDrawer
					deviceID={selectedDevice.uID}
					visible={visible}
					setVisible={setVisible}
					showUpdateLocation={true}
				/>
			)}
		</Fragment>
	) : (
		<Row style={{ height: '100%', alignContent: 'center' }} gutter={0} justify='space-around'>
			<Col style={{ display: 'flex', justifyContent: 'center' }} lg={24} md={24} sm={24} xs={24}>
				<Loader />
			</Col>
		</Row>
	);
};
export default LeafletMap;
